import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import SectionHeroCard from '@/components/SectionHeroCard';

function hasSelfEmployedPath(workType: string, employmentType: string) {
  return workType === 'self-employed' || workType === 'mix' || employmentType === 'self-employed';
}

export default function WorkJourneyCard() {
  const navigate = useNavigate();
  const { data, user } = useApp();
  const [pendingHmrcDeadlineCount, setPendingHmrcDeadlineCount] = useState(0);
  const [pendingEmploymentDeadlineCount, setPendingEmploymentDeadlineCount] = useState(0);
  const [openInvoiceCount, setOpenInvoiceCount] = useState(0);

  useEffect(() => {
    if (!user) return;

    async function fetchDeadlineCount() {
      const [hmrcDeadlineResult, employmentDeadlineResult, invoiceResult] = await Promise.all([
        supabase
          .from('hmrc_deadlines')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('completed', false),
        supabase
          .from('work_deadlines' as any)
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('completed', false),
        supabase
          .from('work_invoices' as any)
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .neq('status', 'paid'),
      ]);

      setPendingHmrcDeadlineCount(hmrcDeadlineResult.count || 0);
      setPendingEmploymentDeadlineCount(employmentDeadlineResult.count || 0);
      setOpenInvoiceCount(invoiceResult.count || 0);
    }

    void fetchDeadlineCount();

    const handleRefresh = () => {
      void fetchDeadlineCount();
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [user]);

  const journey = useMemo(() => {
    const hasWorkSetup = Boolean(data.profile.employmentType || data.profile.workType);
    const selfEmployed = hasSelfEmployedPath(data.profile.workType, data.profile.employmentType);
    const employed = data.profile.employmentType === 'employed' || data.profile.workType === 'employed' || data.profile.workType === 'mix';

    if (!hasWorkSetup) {
      return {
        title: 'Set up your work admin properly',
        description: 'Tell Adulting how you work, then use the right section for deadlines, rights, and difficult conversations.',
        items: [
          { label: 'Save your work setup in your profile', done: false },
          { label: 'Know where your work admin lives', done: false },
          { label: 'Keep one work guide ready for when things go wrong', done: false },
        ],
        actions: [
          { label: 'Edit profile', onClick: () => navigate('/profile'), primary: true },
          { label: 'Open support hub', onClick: () => navigate('/guides?section=work') },
        ],
      };
    }

    if (selfEmployed && openInvoiceCount === 0) {
      return {
        title: 'Get invoicing into the same work flow',
        description: 'Your work setup is saved. Add the invoices you are waiting on so cashflow and chasing do not sit outside the app.',
        items: [
          { label: 'Work setup saved', done: true },
          { label: 'Add your first invoice', done: false },
          { label: 'Keep work scripts ready when payment gets awkward', done: false },
        ],
        actions: [
          { label: 'Open self-employed', onClick: () => navigate('/work?tab=self-employed'), primary: true },
          { label: 'Open seeking work', onClick: () => navigate('/work?tab=tools') },
        ],
      };
    }

    if (selfEmployed && pendingHmrcDeadlineCount === 0) {
      return {
        title: 'Get your self-employed deadlines in place',
        description: 'Your work setup is saved. The next step is making HMRC and payment deadlines visible before they become expensive.',
        items: [
          { label: 'Work setup saved', done: true },
          { label: 'Invoices tracked', done: openInvoiceCount > 0 },
          { label: 'Add or confirm self-employed deadlines', done: false },
        ],
        actions: [
          { label: 'Open self-employed', onClick: () => navigate('/work?tab=self-employed'), primary: true },
          { label: 'Open seeking work', onClick: () => navigate('/work?tab=tools') },
        ],
      };
    }

    if (selfEmployed) {
      return {
        title: 'Your self-employed admin has a live home',
        description: 'Deadlines are being tracked. Keep income current here and use the seeking-work area for packs, drafts, and next steps when your work path shifts.',
        items: [
          { label: 'Work setup saved', done: true },
          { label: 'Invoices tracked', done: openInvoiceCount > 0 },
          { label: 'HMRC deadlines tracked', done: pendingHmrcDeadlineCount > 0 },
          { label: 'Tools ready for tricky work situations', done: true },
        ],
        actions: [
          { label: 'Review deadlines', onClick: () => navigate('/work?tab=self-employed'), primary: true },
          { label: 'Open seeking work', onClick: () => navigate('/work?tab=tools') },
        ],
      };
    }

    if (employed) {
      if (pendingEmploymentDeadlineCount === 0) {
        return {
          title: 'Put your work dates somewhere you can actually see them',
          description: 'Your rights guides are in place. The next step is adding the dates that matter most, like probation, review, renewal, or flexible-working response deadlines.',
          items: [
            { label: 'Work setup saved', done: true },
            { label: 'Employed guidance lives in one place', done: true },
            { label: 'Add your first work deadline', done: false },
            { label: 'Seeking work packs are ready', done: true },
          ],
          actions: [
            { label: 'Open employed', onClick: () => navigate('/work?tab=rights'), primary: true },
            { label: 'Open seeking work', onClick: () => navigate('/work?tab=tools') },
          ],
        };
      }

      return {
        title: 'Your work rights and deadlines live together',
        description: 'For employed work, keep the important dates visible next to the guidance and the seeking-work packs you may want close by.',
        items: [
          { label: 'Work setup saved', done: true },
          { label: 'Employed guidance lives in one place', done: true },
          { label: 'Employment deadlines tracked', done: pendingEmploymentDeadlineCount > 0 },
          { label: 'Seeking work packs are ready', done: true },
        ],
        actions: [
          { label: 'Open employed', onClick: () => navigate('/work?tab=rights'), primary: true },
          { label: 'Open seeking work', onClick: () => navigate('/work?tab=tools') },
        ],
      };
    }

    return {
      title: 'Keep work admin in one place',
      description: 'Adulting can still hold your work setup, deadlines, and guides in one standard flow even if your situation changes over time.',
      items: [
        { label: 'Work setup saved', done: true },
        { label: 'Update your work path when circumstances change', done: false },
        { label: 'Use guides instead of scrambling later', done: false },
      ],
      actions: [
        { label: 'Edit profile', onClick: () => navigate('/profile'), primary: true },
        { label: 'Open support hub', onClick: () => navigate('/guides?section=work') },
      ],
    };
  }, [
    data.profile.employmentType,
    data.profile.workType,
    navigate,
    openInvoiceCount,
    pendingEmploymentDeadlineCount,
    pendingHmrcDeadlineCount,
  ]);

  return (
    <SectionHeroCard
      title="My Work"
      subtitle="Employed, self-employed, and job-search support in one work home"
      stats={[
        {
          label: 'Deadlines',
          value: String(pendingHmrcDeadlineCount + pendingEmploymentDeadlineCount),
          tone: pendingHmrcDeadlineCount + pendingEmploymentDeadlineCount > 0 ? 'danger' : 'success',
        },
        {
          label: 'Invoices',
          value: String(openInvoiceCount),
          tone: openInvoiceCount > 0 ? 'accent' : 'default',
        },
        {
          label: 'Work type',
          value: data.profile.workType === 'mix' ? 'Mixed' : data.profile.workType === 'not-working' ? 'Paused' : (data.profile.workType || 'Add'),
          tone: data.profile.workType ? 'success' : 'danger',
        },
      ]}
      actions={journey.actions}
    />
  );
}
